import $ from 'jquery';
import _ from 'underscore';
import { levelAtConstant } from '@bingads-webui-campaign/scope-constants';
import { withUiBlock } from '@bingads-webui/uiblocker';
import { RecommendationInlineViewFactory } from '@bingads-webui-campaign/recommendation-inline-view-factory';
import {
  RECOMMENDATION_TYPES,
  ADINSIGHT_LOG_ACTION_TYPE,
  CHANNEL_TYPES,
} from '@bingads-webui-campaign/recommendation-core';
import {
  showApplyFeedbackPopup,
} from '@bingads-webui-campaign-react/recommendation-feedback-modal';
import containerTemplate from './container.pug';
import {
  getGoogleImportUri,
  getSetupConvTrackingUri,
  getUETTagUri,
  getFacebookImportUri,
  getCreateAdUri,
} from './external-feature';

function getRowByKey(gridView, key) {
  return gridView.$(`tr[data-key=${key}]`);
}

function preventConfirm(i18n) {
  return window.confirm(i18n.getString('Recommendation_On_Blur_Confirm_Message')); // eslint-disable-line
}

export const recommendationInlineAction = definePlugin => definePlugin('recommendationInlineAction', [
  'config',
  'gridView',
], ({
  selection,
  columns,
  plugins: {
    recommendationInlineAction: {
      i18n,
      newI18n,
      deps,
      gridService,
      logOpportunityEvent,
      router,
      isMCC,
      readOnly,
      el,
      perfMarker,
      serverContext,
      getGoalCreationRecommendationWizard,
      s$i18n,
      viewUET,
    },
  },
}, gridView) => {
  const currentInlineView = {
    key: null,
    view: null,
    $inlineViewContainer: null,
  };

  function initCurrentInlineView() {
    if (currentInlineView.view) {
      currentInlineView.view.remove();
      currentInlineView.view = null;
      if (!gridView.$('input.select-all').prop('checked')) { // if user click select-all checkbox, do not deselect current expanding view
        gridView.deselectRow(currentInlineView.key);
      }
    }
    if (currentInlineView.$inlineViewContainer) {
      currentInlineView.$inlineViewContainer.remove();
      currentInlineView.$inlineViewContainer = null;
    }
    currentInlineView.key = '';
    gridView.$('tr[data-key]').show();
  }
  gridView.on('willUpdate', initCurrentInlineView);


  function preventUserAction(e) {
    const anyChanged = currentInlineView.view ? currentInlineView.view.hasChanged() : false;

    if (anyChanged && !preventConfirm(i18n)) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    return true;
  }

  function inlineActionHandler(e) {
    if (!preventUserAction(e)) {
      return;
    }
    e.stopPropagation();
    initCurrentInlineView();

    const colspan = columns.length;
    const key = $(e.target).parents('tr[data-key]').data('key');
    const recommendation = gridView.itemWithKey(key);
    const $inlineViewContainer = $(containerTemplate({ key, colspan, selection }));
    $inlineViewContainer.bind('DOMSubtreeModified', () => {
      gridView.trigger('didUpdate');
    });

    function appendView() {
      // insert view and hide row.
      const $row = gridView.$(`tr[data-key=${key}]`);
      const view = new RecommendationInlineViewFactory({
        deps,
        i18n,
        newI18n,
        gridService,
        recommendation,
        logOpportunityEvent,
        router,
        isMCC,
        gridView,
        readOnly,
        perfMarker,
        serverContext,
        getGoalCreationRecommendationWizard,
        s$i18n,
        viewUET,
      }).render();
      $inlineViewContainer.find('td.inline-content').append(view.$el);

      currentInlineView.view = view;
      currentInlineView.key = key;
      currentInlineView.$inlineViewContainer = $inlineViewContainer;
      $row.after($inlineViewContainer);
      $row.hide();

      view.on('cancel', () => {
        getRowByKey(gridView, currentInlineView.key).show();
        initCurrentInlineView();
      });

      view.on('didApply', () => {
        initCurrentInlineView();
        gridView.refresh({ force: true });
      });

      view.on('didDismiss', () => {
        initCurrentInlineView();
        gridView.refresh({ force: true });
      });
      gridView.trigger('didUpdate');
    }


    const selectedKeys = gridView.selectedKeys();
    if (_.size(selectedKeys) === 0) {
      appendView();
    } else {
      gridView.deselectAll();
      gridView.once('didUpdate', appendView);
    }
    // [TODO] delete code after confirm with PM
    // if (_.isEmpty(gridView.selectedKeys())) {
    //   appendView();
    // } else {
    //   gridView.deselectAll();
    //   gridView.once('didUpdate', appendView);
    // }
  }

  function applyRecommendation(opportunities, userInputs = [], context = {}, editor = null) {
    if (!_.isNull(editor)) {
      editor.remove();
    }
    return gridService.apply({ opportunities, userInputs, context }).then(() => {
      gridView.refresh({ force: true });
    }).finally(withUiBlock(el));
  }

  function imageExtensionApplyConfirmHandler(opportunities, userInputs = [], editor = null) {
    const { scope } = gridService.recommendationService;
    const level = scope ? scope.levelAt : levelAtConstant.ACCOUNT;
    const adGroupIdVNext = scope && scope.adGroupId ? [scope.adGroupId] : [];
    const campaignAdGroups = level !== levelAtConstant.ACCOUNT ? [{
      Key: scope.campaignId,
      Value: adGroupIdVNext,
    }] : [];
    const options = {
      odata: deps.odata,
      cid: scope.advertiserCustomerId,
      aid: scope.accountId,
      permissions: deps.permissions,
      isFromImgExtV2: true,
      i18n,
      el: '.recommendation-apply-feedback-popup',
      data: {
        type: RECOMMENDATION_TYPES.IMAGE_EXTENSION,
        channel: CHANNEL_TYPES.RECOMMENDATION,
        campaignAdGroups,
      },
      dataService: gridService.recommendationService,
      callback: !_.isEmpty(userInputs) && !_.isNull(editor) ?
        _.partial(applyRecommendation, _, userInputs, _, editor) : applyRecommendation,
      newI18n,
      opportunities,
    };
    showApplyFeedbackPopup(options);
  }

  function applyHandler(e) {
    if (!preventUserAction(e)) {
      return null;
    }
    e.stopPropagation();
    const $row = $(e.target).parents('tr[data-key]');
    const key = $row.data('key');
    const recommendation = gridView.itemWithKey(key);
    const opportunities = recommendation.Opportunities;

    if (gridService.recommendationType === RECOMMENDATION_TYPES.IMAGE_EXTENSION &&
      opportunities[0].ImageSourceStr === 'Website'
      && deps.permissions.IsAccountEnabledForCrawlImagesFromLandingPage) {
      imageExtensionApplyConfirmHandler(opportunities);
      return null;
    }

    return applyRecommendation(opportunities);
  }

  function externalHandler(e) {
    const $row = $(e.target).parents('tr[data-key]');
    const $inlineContainer = $(e.target).parents('tr[data-inlinekey]');
    const key = $row.data('key') || $inlineContainer.data('inlinekey');
    const recommendation = gridView.itemWithKey(key);
    const opportunities = recommendation.Opportunities;
    const opportunityId = opportunities[0].OpportunityId;
    const targetAccountId = opportunities[0].Target.AccountId;
    const statusId = opportunities[0].StatusId;
    const campaignId = opportunities[0].Target.CampaignId;
    const adGroupId = opportunities[0].Target.AdGroupId;

    const actionName = {
      [RECOMMENDATION_TYPES.GOOGLE_IMPORT]: 'Get Started',
      [RECOMMENDATION_TYPES.GOOGLE_IMPORT_SCHEDULED]: 'Get Started',
      [RECOMMENDATION_TYPES.SETUP_CONVERSION_TRACKING_OPPORTUNITY]: isMCC ? 'Check details' : 'Get Started',
      [RECOMMENDATION_TYPES.FIX_CONVERSION_TRACKING_OPPORTUNITY]: 'View UET Tags',
      [RECOMMENDATION_TYPES.FACEBOOK_IMPORT]: 'Get Started',
      [RECOMMENDATION_TYPES.REPAIR_ADS]: 'Create ad',
    };

    const context = {
      ButtonName: actionName[gridService.recommendationType],
    };

    logOpportunityEvent({
      type: gridService.recommendationType,
      action: ADINSIGHT_LOG_ACTION_TYPE.CUSTOM,
      opportunityId,
      context,
    });

    switch (gridService.recommendationType) {
      case RECOMMENDATION_TYPES.GOOGLE_IMPORT:
      case RECOMMENDATION_TYPES.GOOGLE_IMPORT_SCHEDULED:
        window.location = getGoogleImportUri(
          gridService.recommendationType,
          gridService.recommendationService.scope.customerId,
          gridService.recommendationService.scope.userId,
          targetAccountId,
          opportunityId
        );
        break;
      case RECOMMENDATION_TYPES.SETUP_CONVERSION_TRACKING_OPPORTUNITY:
        window.open(getSetupConvTrackingUri({
          recommendationType: gridService.recommendationType,
          customerId: gridService.recommendationService.scope.customerId,
          userId: gridService.recommendationService.scope.userId,
          accountId: targetAccountId,
          opportunityId,
          statusId,
        }));
        break;
      case RECOMMENDATION_TYPES.FIX_CONVERSION_TRACKING_OPPORTUNITY:
        window.open(getUETTagUri(
          gridService.recommendationService.scope.customerId,
          gridService.recommendationService.scope.userId,
          targetAccountId,
          opportunityId,
          isMCC
        ));
        break;
      case RECOMMENDATION_TYPES.FACEBOOK_IMPORT:
        window.location = getFacebookImportUri(
          gridService.recommendationType,
          gridService.recommendationService.scope.customerId,
          gridService.recommendationService.scope.userId,
          targetAccountId,
          opportunityId
        );
        break;
      case RECOMMENDATION_TYPES.REPAIR_ADS:
        window.open(getCreateAdUri(
          gridService.recommendationService.scope.customerId,
          gridService.recommendationService.scope.userId,
          targetAccountId,
          campaignId,
          adGroupId
        ), '_blank');
        break;
      default:
        break;
    }
  }

  function panelHandler(e, dataKey = null) {
    let $row;
    let $inlineContainer;
    let key;
    if (!_.isNull(dataKey)) {
      key = dataKey;
    } else {
      $row = $(e.target).parents('tr[data-key]');
      $inlineContainer = $(e.target).parents('tr[data-inlinekey]');
      key = $row.data('key') || $inlineContainer.data('inlinekey');
    }
    const recommendation = gridView.itemWithKey(key);
    const opportunities = recommendation.Opportunities;
    const targetAccountId = opportunities[0].Target.AccountId;
    const campaignId = opportunities[0].Target.CampaignId;
    const adGroupId = opportunities[0].Target.AdGroupId;
    const opportunityId = opportunities[0].OpportunityId;
    const type = gridService.recommendationType;
    switch (gridService.recommendationType) {
      case RECOMMENDATION_TYPES.IMAGE_EXTENSION: {
        const context = recommendation.ImageSourceStr === 'StockImage' ? {
          ImageSource: recommendation.ImageSourceStr,
          ShutterstockQuery: _.first(_.first(opportunities).StockImages).Query,
          ShutterstockTotalCount: _.first(_.first(opportunities).StockImages).TotalCount,
          ShutterstockImageId: _.first(_.first(opportunities).StockImages).ImageId,
        } : {
          ImageSource: recommendation.ImageSourceStr,
        };
        logOpportunityEvent({
          opportunityId,
          campaignId,
          type,
          action: ADINSIGHT_LOG_ACTION_TYPE.VIEWEDIT,
          accountId: targetAccountId,
          context,
        });
        gridView.deselectAll();
        const apply = (userInputs, editor) => {
          if (gridService.recommendationType === RECOMMENDATION_TYPES.IMAGE_EXTENSION &&
            opportunities[0].ImageSourceStr === 'Website'
            && _.some(_.first(userInputs).Images, item => _.isUndefined(item.ImageAssetId))
            && deps.permissions.IsAccountEnabledForCrawlImagesFromLandingPage) {
            imageExtensionApplyConfirmHandler(opportunities, userInputs, editor);
          } else {
            applyRecommendation(opportunities, userInputs, {}, editor);
          }
        };
        import(/* webpackMode: 'eager' */'./image-extension').then(({ openImageExtensionEditor }) => {
          openImageExtensionEditor(
            opportunities,
            targetAccountId,
            campaignId,
            adGroupId,
            type,
            i18n,
            newI18n,
            this.editor,
            apply,
            readOnly
          );
        });
        break;
      }
      case RECOMMENDATION_TYPES.MULTIMEDIA_ADS:
      case RECOMMENDATION_TYPES.RESPONSIVE_SEARCH_ADS:
      case RECOMMENDATION_TYPES.IMPROVE_RESPONSIVE_SEARCH_ADS:
      case RECOMMENDATION_TYPES.IMPROVE_MULTIMEDIA_ADS: {
        logOpportunityEvent({
          opportunityId,
          campaignId,
          type,
          action: ADINSIGHT_LOG_ACTION_TYPE.VIEWEDIT,
          accountId: targetAccountId,
        });
        gridView.deselectAll();
        const apply = (data, userInputs, context, editor) => {
          applyRecommendation(data, userInputs, context, editor);
        };
        import(/* webpackMode: 'eager' */'./new-ads-view').then(({ openNewAdsViewEditor }) => {
          openNewAdsViewEditor(
            deps,
            i18n,
            newI18n,
            type,
            gridService,
            recommendation,
            router,
            isMCC,
            gridView,
            readOnly,
            gridView.editor,
            apply
          );
        });
        break;
      }
      case RECOMMENDATION_TYPES.CREATE_CONVERSION_GOAL: {
        logOpportunityEvent({
          opportunityId,
          campaignId,
          type,
          action: ADINSIGHT_LOG_ACTION_TYPE.VIEWEDIT,
          accountId: targetAccountId,
        });
        gridView.deselectAll();
        const apply = (data, userInputs, context, editor) => {
          applyRecommendation(data, userInputs, context, editor);
        };
        import(/* webpackMode: 'eager' */'./create-conversion-goal').then(({ openCreateConversionGoalEditor }) => {
          openCreateConversionGoalEditor(
            deps,
            i18n,
            newI18n,
            type,
            gridService,
            recommendation,
            router,
            isMCC,
            gridView,
            readOnly,
            this.editor,
            apply,
            perfMarker,
            serverContext,
            getGoalCreationRecommendationWizard
          );
        });
        break;
      }
      default:
        break;
    }
  }

  gridView.on('openEditPanel', key => panelHandler(null, key));

  const actionColumnProjection = {
    name: 'actionColumnProjection',
    handler: function actionColumn(state) {
      const patch = {};
      patch.events = _.defaults({
        'click .inline-action-view': inlineActionHandler,
        'click .inline-action-apply': applyHandler,
        'click .inline-action-external': externalHandler,
        'click .inline-action-panel': panelHandler,
      }, state.events);

      return _.defaults(patch, state);
    },
  };

  const preventGridActionProjection = {
    name: 'preventGridActionProjection',
    handler: function testContent(state) {
      const events = _.defaults({
        'click th input.select-all': preventUserAction,
        'click tbody tr[data-key] td': preventUserAction,
      }, state.events);

      return _.defaults({ events }, state);
    },
  };

  gridView.pipeStructureProjections(actionColumnProjection);
  gridView.pipeContentProjections(preventGridActionProjection);

  return currentInlineView;
});
